import { Flex } from '@chakra-ui/react';
import { Input, Text } from '@kula-ai/components';
import { useField } from 'formik';
import { addYears } from 'date-fns';
import { useDateFormat } from '@/hooks';
import {
  DateRangePicker,
  resolvePortalTarget,
  toFormatHint,
} from '@/design-system/date-picker';
import type { DateRangeValue } from '@/design-system/date-picker';

type FilterDateRangeItem = {
  name: string;
  /** Lifts the no-future-dates cap, same as the single-date filter field. */
  isCustomize?: boolean;
  /** Also lifts the cap, and opens the year grid up to a decade ahead. */
  showAllDates?: boolean;
  /** The filter menu's own node, so the panel stays inside it. */
  containerRef?: React.RefObject<HTMLElement>;
};

const FUTURE_YEARS = 10;

/**
 * The `filterDateRangePicker` form field: a read-only range input that opens
 * the shared `DateRangePicker`. Everything below is the Formik binding and
 * the filter-specific date bounds — the panel itself is shared.
 */
export const FilterDateRangePicker = ({
  item,
  field,
}: {
  item: FilterDateRangeItem;
  field?: { value?: DateRangeValue };
}) => {
  const { name, isCustomize, showAllDates = false, containerRef } = item;
  const [, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const { dateFnsFormat } = useDateFormat();

  const value = (meta?.value ?? field?.value) as DateRangeValue | undefined;
  const isError = Boolean(
    meta?.touched && meta?.error && (!value?.from || !value?.to)
  );

  const hint = toFormatHint(dateFnsFormat);
  const today = new Date();
  const allowsFuture = Boolean(isCustomize || showAllDates);

  const commit = (next: DateRangeValue) => {
    setValue(next);
    setTouched(true);
    setError(undefined);
  };

  return (
    <Flex flexDir="column" w="100%">
      <DateRangePicker
        value={value}
        onChange={commit}
        maxDate={allowsFuture ? addYears(today, FUTURE_YEARS) : today}
        portalTarget={resolvePortalTarget(containerRef?.current)}
        renderTrigger={({ onClick, rangeLabel }) => (
          <Input
            id={name}
            readOnly
            error={isError}
            value={rangeLabel}
            placeholder={`${hint} - ${hint}`}
            onClick={onClick}
            onFocus={(event) => event.preventDefault()}
          />
        )}
      />
      {isError && (
        <Text variant="small" color="error" className="mt-2">
          {meta.error as string}
        </Text>
      )}
    </Flex>
  );
};
